import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { RootState } from '@cp/src/store';
import {
  clearFeedbackInteractionReferenceId,
  feedbackSubmitted,
  resetCall as resetCallAction,
  syncFromStorage
} from '@cp/src/reducers/whatsappCallSlice';
import { ECallingType } from '../../../interfaces/index.type';
import { ITelePhoneData } from '../../../../interfaces/CaseDetail.type';
import { WACallStates, WACallViewState, WAStorageState, WhatsappCallControls } from './interface';
import { useCrossTabSync } from './useCrossTabSync';
import { resetMediaState } from './mediaManager';
import { BLOCKING_STATUSES, getTabId } from './utils';
import {
  cancelAndReset,
  completePendingDisconnect,
  disconnectCall,
  grantTabShare as coordinatorGrantTabShare,
  initCallCoordinator,
  startCall as coordinatorStartCall
} from './callCoordinator';

interface UseWhatsappCallParams {
  loanId: string;
  customerId: string;
  telephoneRefId?: string;
  telephones?: { data?: ITelePhoneData[] };
  currentCallType?: ECallingType;
  setCurrentCallType?: (type: ECallingType) => void;
  telephone?: string;
}

export function useWhatsappCall({
  loanId,
  customerId,
  currentCallType,
  setCurrentCallType,
  telephone
}: UseWhatsappCallParams): WhatsappCallControls {
  const dispatch = useDispatch();
  const callState = useSelector((state: RootState) => state.whatsappCall);

  useEffect(() => {
    initCallCoordinator();
    completePendingDisconnect();
  }, []);

  const handleStateSync = useCallback((stored: WAStorageState) => {
    dispatch(syncFromStorage(stored));
  }, []);

  const handleStateCleared = useCallback(() => {
    dispatch(resetCallAction());
  }, []);

  const handleDisconnectCommand = useCallback(() => {
    disconnectCall();
  }, []);

  const {
    isCallActiveElsewhere,
    isSameCustomerCall,
    isOwnTabCallForDifferentCustomer,
    sendDisconnectCommand
  } = useCrossTabSync({
    loanId,
    customerId,
    currentStatus: callState.status,
    onStateSync: handleStateSync,
    onStateCleared: handleStateCleared,
    onDisconnectCommand: handleDisconnectCommand
  });

  // True when the active Redux call belongs to this hook's customer (or no call is active).
  const isCallForThisCustomer =
    !callState.callLoanId ||
    (callState.callLoanId === loanId && callState.callCustomerId === customerId);

  const isCallForCurrentCustomer =
    callState.callLoanId === loanId && callState.callCustomerId === customerId;

  // Telephone change → soft reset only if the active call belongs to this customer and isn't in a blocking (active) state.
  const isFirstTelephoneMount = useRef(true);
  useEffect(() => {
    if (isFirstTelephoneMount.current) {
      isFirstTelephoneMount.current = false;
      return;
    }
    if (!isCallForThisCustomer) return;
    if (BLOCKING_STATUSES.includes(callState.status)) return;
    dispatch(resetCallAction());
    resetMediaState();
  }, [telephone]);

  // Call-type transitions for feedback lifecycle — guarded so a different
  // customer's panel never resets the active call's state.
  const prevCallType = useRef(currentCallType);
  useEffect(() => {
    const prev = prevCallType.current;
    prevCallType.current = currentCallType;

    if (!isCallForThisCustomer) return;
    if (
      callState.status === WACallStates.AWAITING_FEEDBACK &&
      currentCallType !== undefined &&
      currentCallType !== ECallingType.WHATSAPP_CALL
    ) {
      dispatch(clearFeedbackInteractionReferenceId());
      dispatch(resetCallAction());
      resetMediaState();
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_Reset, {
        reason: 'new_call_type_selected',
        customerId
      });
      return;
    }

    // currentCallType was cleared after being WHATSAPP_CALL → feedback was submitted.
    if (!currentCallType && prev === ECallingType.WHATSAPP_CALL) {
      dispatch(clearFeedbackInteractionReferenceId());
      dispatch(resetCallAction());
      resetMediaState();
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_FeedbackSubmitted, {
        phone: callState.pendingPhone,
        customerId
      });
      return;
    }

    // Generic feedback-submitted transition (callType cleared, call had completed).
    if (!currentCallType && prev !== undefined && callState.isCallCompleted) {
      dispatch(feedbackSubmitted());
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_FeedbackSubmitted, {
        phone: callState.pendingPhone,
        customerId
      });
    }
  }, [currentCallType]);

  // When the call transitions to awaiting_feedback for THIS customer, switch the parent's call-type so the WhatsApp feedback form is shown.
  useEffect(() => {
    if (
      callState.status === WACallStates.AWAITING_FEEDBACK &&
      callState.callLoanId === loanId &&
      callState.callCustomerId === customerId
    ) {
      setCurrentCallType?.(ECallingType.WHATSAPP_CALL);
    }
  }, [
    callState.status,
    callState.callLoanId,
    callState.callCustomerId,
    loanId,
    customerId,
    setCurrentCallType
  ]);

  const startCall = useCallback(
    (phone: string) => {
      if (isCallActiveElsewhere || isSameCustomerCall) {
        toast.error('A WhatsApp call is already active in another tab.');
        return;
      }
      coordinatorStartCall(phone, loanId, customerId);
    },
    [customerId, loanId, isCallActiveElsewhere, isSameCustomerCall]
  );

  const grantTabShare = useCallback(async () => {
    if (isCallActiveElsewhere || isSameCustomerCall) {
      toast.error('A WhatsApp call is already active in another tab.');
      return;
    }
    await coordinatorGrantTabShare();
  }, [isCallActiveElsewhere, isSameCustomerCall]);

  const isOwnerTab = callState.ownerTabId === getTabId();

  const disconnect = useCallback(() => {
    if (isOwnerTab) {
      disconnectCall();
    } else {
      sendDisconnectCommand();
    }
  }, [isOwnerTab, sendDisconnectCommand]);

  const resetCall = useCallback(() => {
    cancelAndReset('manual_reset');
  }, []);

  const viewState: WACallViewState = useMemo(
    () => ({
      status: callState.status,
      error: callState.error,
      recordingUrl: callState.recordingUrl,
      recordingMimeType: callState.recordingMimeType,
      isCallCompleted: callState.isCallCompleted
    }),
    [
      callState.status,
      callState.error,
      callState.recordingUrl,
      callState.recordingMimeType,
      callState.isCallCompleted
    ]
  );

  const isCallDisabled =
    isCallActiveElsewhere ||
    isSameCustomerCall ||
    isOwnTabCallForDifferentCustomer ||
    callState.isCallCompleted ||
    BLOCKING_STATUSES.includes(callState.status);

  return {
    state: viewState,
    startCall,
    grantTabShare,
    disconnect,
    resetCall,
    isCallDisabled,
    isCallActiveElsewhere,
    isSameCustomerCall,
    isOwnTabCallForDifferentCustomer,
    isCallForCurrentCustomer
  };
}
